import {
  InterventionBrute,
  InterventionCurative,
  reunirInterventions,
  libelleTypeIntervention,
} from '../../Services/interventions';
import { InterventionActualisation } from '../../Services/intervention-actualisation';
import { InterventionAcces } from '../../Composants/intervention-acces/intervention-acces';
import { InterventionSuppression } from '../../Composants/intervention-suppression/intervention-suppression';
import {
  InterventionEditable,
  creerBrouillonIntervention,
} from '../../Services/intervention-edition';
import { jourPlanification } from '../../Services/intervention-dates';
import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Authentification } from '../../Services/authentification';
import {
  formaterDateAvecJour,
  formaterDateHeure,
  formaterDateLongueAvecJour,
  formaterHeure,
  formaterHorairesIntervention,
  ticketCodeEtatLabel,
  ticketStatusClass,
  ticketStatusLabel,
} from '../../Services/format';
import { Installation as InstallationService } from '../../Services/installation';
import { Contact, Installation, TicketBrut } from '../../Services/EntiteProjet';
import { SpinnerOverlayComponent } from '../../Composants/spinner-overlay/spinner-overlay';
import { TicketCardItem } from '../../Composants/silo-tickets/silo-tickets';
import { EtatService } from '../../Services/etat';
import { TicketService } from '../../Services/ticket';
import { CONFIG_ETATS_TICKET, TicketColumnState, TicketState } from '../../Services/ticket-state';
import { TicketEvenement } from '../../Composants/ticket-evenements/ticket-evenements';
import { masquerFermeturesRedondantes } from './ticket-evenements';
import {
  InterventionModalComponent,
  InterventionModalData,
} from '../../Composants/intervention-modal/intervention-modal';

type Intervention = {
  id: number;
  titre?: string;
  auteurId?: number | null;
  idIntervenant?: number | null;
  date_intervention: string;
  date_deb?: string | null;
  date_fin?: string | null;
  intervenant: string;
  commentaire: string | null;
  pb_resolu: boolean | null;
  nature?: 'message' | 'bon_intervention' | 'bon' | 'planification';
  categorie?: 'curative' | 'preventive';
};

export type EntreeFeed =
  | { type: 'intervention'; date: Date; intervention: Intervention }
  | { type: 'evenement'; date: Date; evenement: TicketEvenement };

@Component({
  selector: 'app-ticket-detail',
  imports: [
    SpinnerOverlayComponent,
    RouterLink,
    FormsModule,
    InterventionModalComponent,
    InterventionAcces,
    InterventionSuppression,
  ],
  templateUrl: './ticket-detail.html',
  styleUrls: ['./ticket-detail.css', './ticket-detail-interventions.css'],
  host: { '(document:click)': 'onDocumentClick($event)' },
})
export class TicketDetail implements OnInit {
  private readonly actualisationInterventions = inject(InterventionActualisation);
  private readonly messageTextarea = viewChild<ElementRef<HTMLTextAreaElement>>('messageTextarea');
  private readonly menuStatutContainer = viewChild<ElementRef<HTMLElement>>('menuStatutContainer');

  private route = inject(ActivatedRoute);
  private installationService = inject(InstallationService);
  private ticketService = inject(TicketService);
  private auth = inject(Authentification);
  readonly etat = inject(EtatService);

  private readonly destroyRef = inject(DestroyRef);
  private contexteId = 0;
  private chargementTicketId = 0;
  private chargementInstallationId = 0;
  ticketId: string | null = null;

  readonly ticket = signal<TicketCardItem | null>(null);
  readonly siloState = signal<TicketColumnState | null>(null);

  private contexteValide() {
    const contexte = this.contexteId;
    const session = this.auth.sessionVersion;
    return () =>
      !this.destroyRef.destroyed &&
      contexte === this.contexteId &&
      session === this.auth.sessionVersion;
  }

  readonly installation = signal<Installation | null>(null);
  readonly chargement = signal(false);
  readonly erreurChargement = signal('');
  private readonly interventions = signal<Intervention[]>([]);
  private readonly evenements = signal<TicketEvenement[]>([]);
  readonly historiqueInterventions = signal<InterventionBrute[]>([]);
  private readonly planificationsCuratives = signal<InterventionCurative[]>([]);
  /** La liste dédiée du ticket prime sur une ancienne classification reçue dans l’historique. */
  readonly interventionsDuTicket = computed(() => {
    const uniques = new Map<string, InterventionBrute>();
    for (const intervention of [
      ...this.historiqueInterventions(),
      ...this.planificationsCuratives(),
    ])
      uniques.set(`${intervention.type}-${intervention.id}`, intervention);
    return [...uniques.values()];
  });
  private readonly idsPlanificationsCuratives = computed(
    () =>
      new Set(
        this.interventionsDuTicket()
          .filter((i) => i.type === 'intervention_curative' && i.nature === 'planification')
          .map((i) => i.id),
      ),
  );
  readonly sectionsInterventions = computed(() => {
    const maintenant = Date.now();
    const date = (intervention: InterventionBrute) =>
      new Date(intervention.date_deb ?? intervention.date_intervention).getTime();
    const interventions = this.interventionsDuTicket();
    // Une planification sans heure reste à venir toute la journée prévue.
    const aVenir = (i: InterventionBrute) =>
      i.nature === 'planification' && !i.date_deb
        ? i.date_intervention.slice(0, 10) >= jourPlanification(new Date(maintenant))
        : date(i) > maintenant;
    return [
      {
        titre: 'Passées',
        vide: 'Aucune intervention passée.',
        interventions: interventions.filter((i) => !aVenir(i)).sort((a, b) => date(b) - date(a)),
      },
      {
        titre: 'À venir',
        vide: 'Aucune intervention à venir.',
        interventions: interventions.filter((i) => aVenir(i)).sort((a, b) => date(a) - date(b)),
      },
    ];
  });
  readonly chargementHistorique = signal(false);
  readonly erreurHistorique = signal('');
  readonly libelleTypeIntervention = libelleTypeIntervention;
  private requeteHistorique = 0;
  private installationHistoriqueId: number | null = null;
  readonly chargementFeed = signal(false);
  readonly message = signal('');
  readonly envoiMessage = signal(false);
  readonly erreurMessage = signal('');
  readonly interventionSelectionnee = signal<Intervention | null>(null);
  readonly interventionModalData = computed<InterventionModalData | null>(() => {
    const intervention = this.interventionSelectionnee();
    if (!intervention) return null;

    return {
      ...(intervention.nature === 'planification' ? { nature: 'planification' as const } : {}),
      title: intervention.titre ?? this.formaterPhraseInterventionFeed(intervention),
      informations: [
        {
          label: 'Date',
          value: formaterDateLongueAvecJour(
            intervention.date_deb ?? intervention.date_intervention,
          ),
          icon: 'event',
        },
        {
          label: 'Horaire',
          value: formaterHorairesIntervention(intervention.date_deb, intervention.date_fin),
          icon: 'schedule',
        },
        {
          label: 'Intervenant',
          value: intervention.intervenant || 'Non renseigné',
          icon: 'badge',
        },
        { label: 'Statut', value: this.statutInterventionModal(intervention), icon: 'task_alt' },
      ],
      detailsLabel: 'Détails',
      details: intervention.commentaire?.trim() || 'Aucun détail disponible.',
      referencePhotos: this.estBonIntervention(intervention)
        ? { id: intervention.id, categorie: 'curative' }
        : intervention.categorie
          ? { id: intervention.id, categorie: intervention.categorie }
          : undefined,
    };
  });
  readonly annuaireOuvert = signal(false);
  readonly masquerEvenements = signal(false);
  readonly masquerBonsIntervention = signal(false);
  readonly menuStatutOuvert = signal(false);
  readonly changementStatutEnCours = signal(false);
  readonly erreurStatut = signal('');

  readonly groupesContacts: {
    label: string;
    key: keyof Pick<Installation, 'mos' | 'bes' | 'installateurs' | 'mainteneurs'>;
  }[] = [
    { label: "Maîtres d'ouvrage", key: 'mos' },
    { label: "Bureaux d'études", key: 'bes' },
    { label: 'Installateurs', key: 'installateurs' },
    { label: 'Mainteneurs', key: 'mainteneurs' },
  ];

  readonly formaterDateHeure = formaterDateHeure;
  readonly horairesHistorique = formaterHorairesIntervention;
  dateHistorique(valeur: string): string {
    const date = new Date(valeur);
    return Number.isNaN(date.getTime())
      ? 'Date inconnue'
      : new Intl.DateTimeFormat('fr-FR', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        }).format(date);
  }
  readonly ticketStatusLabel = ticketStatusLabel;
  readonly ticketStatusClass = ticketStatusClass;
  readonly utilisateurConnecteId = computed(() => this.auth.getUtilisateurConnecteId());
  private readonly evenementsAffiches = computed(() =>
    masquerFermeturesRedondantes(this.evenements()).filter(
      (evenement) => !this.estEvenementMasque(evenement),
    ),
  );
  readonly nombreEvenements = computed(() => this.evenementsAffiches().length);
  readonly nombreBonsIntervention = computed(
    () =>
      this.interventionsDuFil().filter((intervention) => this.estBonIntervention(intervention))
        .length,
  );
  /** Une planification ne doit jamais compter comme un bon, même si les deux listes la répètent. */
  private readonly interventionsDuFil = computed(() =>
    this.interventions().filter(
      (i) =>
        i.nature === 'message' ||
        (i.nature !== 'planification' && !this.idsPlanificationsCuratives().has(i.id)),
    ),
  );
  readonly transitionsStatutDisponibles = computed<TicketColumnState[]>(() => {
    const etat = this.siloState();
    if (!etat) return [];
    return CONFIG_ETATS_TICKET[etat].transitionsAutorisees;
  });
  readonly premierMessage = computed<Intervention | null>(() => {
    const messages = this.interventionsDuFil().filter(
      (intervention) => !this.estBonIntervention(intervention),
    );
    return messages[0] ?? null;
  });

  readonly feed = computed<EntreeFeed[]>(() => {
    const masquerEvenements = this.masquerEvenements();
    const masquerBonsIntervention = this.masquerBonsIntervention();
    const premierMessageId = this.premierMessage()?.id;

    const merged: EntreeFeed[] = [
      ...this.interventionsDuFil().map((i): EntreeFeed => ({
        type: 'intervention',
        date: new Date(i.date_intervention),
        intervention: i,
      })),
      ...this.evenementsAffiches().map((e): EntreeFeed => ({
        type: 'evenement',
        date: new Date(e.date),
        evenement: e,
      })),
    ];

    return merged
      .filter((entree) => {
        if (
          premierMessageId != null &&
          entree.type === 'intervention' &&
          !this.estBonIntervention(entree.intervention) &&
          entree.intervention.id === premierMessageId
        ) {
          return false;
        }

        if (masquerEvenements && entree.type === 'evenement') return false;
        if (
          masquerBonsIntervention &&
          entree.type === 'intervention' &&
          this.estBonIntervention(entree.intervention)
        ) {
          return false;
        }
        return true;
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  });

  ngOnInit() {
    this.actualisationInterventions.changements
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ ticketId }) => {
        if (this.ticketId && Number(this.ticketId) === ticketId)
          void this.chargerTicketComplet(this.ticketId);
      });
    this.route.paramMap
      .pipe(
        map((params) => params.get('ticketId')),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((id) => {
        this.ticketId = id;
        this.reinitialiserDetail();
        const selection = this.etat.ticketSelectionne();
        if (id != null && String(selection?.id) === id) {
          this.ticket.set(selection);
          this.siloState.set(this.etat.siloEtatTicket());
        } else {
          this.etat.ticketSelectionne.set(null);
          this.etat.siloEtatTicket.set(null);
        }
        if (id) void this.chargerTicketComplet(id);
      });
    this.auth.sessionChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.reinitialiserDetail();
    });
  }

  private reinitialiserDetail() {
    ++this.contexteId;
    ++this.chargementTicketId;
    ++this.chargementInstallationId;
    this.ticket.set(null);
    this.siloState.set(null);
    this.installation.set(null);
    this.interventions.set([]);
    this.evenements.set([]);
    ++this.requeteHistorique;
    this.installationHistoriqueId = null;
    this.historiqueInterventions.set([]);
    this.planificationsCuratives.set([]);
    this.chargementHistorique.set(false);
    this.erreurHistorique.set('');
    this.message.set('');
    this.erreurChargement.set('');
    this.erreurMessage.set('');
    this.erreurStatut.set('');
    this.chargement.set(false);
    this.chargementFeed.set(false);
    this.envoiMessage.set(false);
    this.changementStatutEnCours.set(false);
    this.menuStatutOuvert.set(false);
    this.annuaireOuvert.set(false);
    this.interventionSelectionnee.set(null);
    this.masquerEvenements.set(false);
    this.masquerBonsIntervention.set(false);
  }

  get retourUrl(): string[] {
    const installationId = this.installation()?.id;
    if (installationId != null) {
      return ['/installation', String(installationId), 'tickets'];
    }
    return ['/tickets'];
  }

  libelleEtatTransition(state: TicketColumnState): string {
    return CONFIG_ETATS_TICKET[state].label;
  }

  toggleMenuStatut() {
    if (this.transitionsStatutDisponibles().length === 0 || this.changementStatutEnCours()) return;
    this.menuStatutOuvert.update((ouvert) => !ouvert);
    this.erreurStatut.set('');
  }

  onDocumentClick(event: MouseEvent) {
    if (!this.menuStatutOuvert()) return;

    const container = this.menuStatutContainer()?.nativeElement;
    const target = event.target;
    if (!container || !(target instanceof Node)) return;

    if (!container.contains(target)) {
      this.menuStatutOuvert.set(false);
    }
  }

  contactsInstallation(
    key: keyof Pick<Installation, 'mos' | 'bes' | 'installateurs' | 'mainteneurs'>,
  ): Contact[] {
    return this.installation()?.[key] ?? [];
  }

  nombreContactsInstallation(): number {
    return this.groupesContacts.reduce(
      (total, groupe) => total + this.contactsInstallation(groupe.key).length,
      0,
    );
  }

  fermerAnnuaireSurBackdrop(event: MouseEvent) {
    if (event.target === event.currentTarget) this.annuaireOuvert.set(false);
  }

  async changerStatut(targetState: TicketColumnState) {
    const estValide = this.contexteValide();
    const ticketId = this.ticketId;
    const currentState = this.siloState();

    if (!ticketId || !currentState || this.changementStatutEnCours()) return;
    if (!CONFIG_ETATS_TICKET[currentState].transitionsAutorisees.includes(targetState)) return;

    this.changementStatutEnCours.set(true);
    this.erreurStatut.set('');

    try {
      const ticketEnregistre = await this.ticketService.updateTicketState(ticketId, targetState);
      if (!estValide()) return;
      if (!ticketEnregistre) {
        this.erreurStatut.set(
          `Le ticket n'a pas pu être déplacé vers "${this.libelleEtatTransition(targetState)}".`,
        );
        return;
      }

      this.siloState.set(targetState);
      this.etat.siloEtatTicket.set(targetState);
      this.menuStatutOuvert.set(false);
      await this.chargerTicketComplet(ticketId);
    } catch {
      if (!estValide()) return;
      this.erreurStatut.set(
        `Le ticket n'a pas pu être déplacé vers "${this.libelleEtatTransition(targetState)}".`,
      );
    } finally {
      if (estValide()) this.changementStatutEnCours.set(false);
    }
  }

  private async chargerTicketComplet(ticketId: string) {
    const contexteValide = this.contexteValide();
    const requete = ++this.chargementTicketId;
    const estValide = () => contexteValide() && requete === this.chargementTicketId;
    this.chargementFeed.set(true);
    try {
      const data = await this.ticketService.getTicket(ticketId);
      if (!estValide()) return;
      if (!data) throw new Error('Ticket introuvable');
      this.appliquerTicketBrut(data);
    } catch {
      if (estValide()) this.erreurChargement.set('Impossible de charger le ticket.');
    } finally {
      if (estValide()) this.chargementFeed.set(false);
    }
  }

  private appliquerTicketBrut(data: TicketBrut) {
    const ticket = this.mapperTicket(data);
    this.ticket.set(ticket);
    this.etat.ticketSelectionne.set(ticket);
    this.siloState.set(this.mapperEtat(this.lireTexte(data.etat)));
    this.etat.siloEtatTicket.set(this.siloState());

    const installationId = this.lireNombre(data.installation?.id ?? data.id_installation);
    if (installationId == null || this.installation()?.id !== installationId) {
      ++this.chargementInstallationId;
      this.installation.set(null);
      this.chargement.set(false);
      this.erreurChargement.set('');
      if (installationId != null) {
        void this.chargerInstallation(installationId);
      }
    }

    const rawEvenements = data.evenements;
    if (Array.isArray(rawEvenements)) {
      const evenements = (rawEvenements as TicketEvenement[]).sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      );
      this.evenements.set(evenements);
    } else {
      this.evenements.set([]);
    }

    const rawMessages = Array.isArray(data.messages)
      ? data.messages.map((item) => ({ ...(item as Intervention), nature: 'message' as const }))
      : [];
    const bons = Array.isArray(data.bons_intervention) ? data.bons_intervention : [];
    this.planificationsCuratives.set(
      [
        ...bons.filter(
          (item) =>
            !!item &&
            typeof item === 'object' &&
            'nature' in item &&
            item.nature === 'planification',
        ),
        // Les entrées dédiées viennent en dernier pour primer lors de la déduplication.
        ...(Array.isArray(data.planifications_curatives) ? data.planifications_curatives : []),
      ].flatMap((item) => this.lirePlanificationCurative(item)),
    );
    this.installationHistoriqueId = installationId ?? null;
    void this.chargerHistorique();

    const rawBonsIntervention = bons
      .filter(
        (item) =>
          !!item &&
          typeof item === 'object' &&
          (!('nature' in item) || item.nature !== 'planification'),
      )
      .map((item) => ({
        ...(item as Intervention),
        nature: 'bon_intervention' as const,
      }));
    const interventionsBrutes = [...rawMessages, ...rawBonsIntervention];

    if (interventionsBrutes.length > 0) {
      const interventions = (interventionsBrutes as Intervention[])
        .filter(
          (intervention) =>
            this.estBonIntervention(intervention) || intervention.commentaire?.trim(),
        )
        .sort(
          (a, b) =>
            new Date(a.date_intervention).getTime() - new Date(b.date_intervention).getTime(),
        );
      this.interventions.set(interventions);
    } else {
      this.interventions.set([]);
    }
  }

  /** La provenance « planifications_curatives » fait foi, sans déduire la nature des horaires. */
  private lirePlanificationCurative(valeur: unknown): InterventionCurative[] {
    if (!valeur || typeof valeur !== 'object') return [];
    const item = valeur as Record<string, unknown>;
    const id = this.lireNombre(item['id']);
    const date = this.lireTexte(item['date_intervention']);
    if (!id || !Number.isSafeInteger(id) || id <= 0 || !date || !Number.isFinite(Date.parse(date)))
      return [];
    return [
      {
        id,
        nature: 'planification',
        type: 'intervention_curative',
        date_intervention: date,
        date_deb: this.lireTexte(item['date_deb']) ?? null,
        date_fin: this.lireTexte(item['date_fin']) ?? null,
        commentaire: this.lireTexte(item['commentaire']) ?? null,
        intervenant: this.lireTexte(item['intervenant']) ?? '',
        pb_resolu: typeof item['pb_resolu'] === 'boolean' ? item['pb_resolu'] : null,
        ticket: { id: Number(this.ticketId), numero: this.ticket()?.reference ?? '' },
      },
    ];
  }

  async chargerHistorique(): Promise<void> {
    const requete = ++this.requeteHistorique;
    const contexteValide = this.contexteValide();
    const installationId = this.installationHistoriqueId;
    const ticketId = Number(this.ticketId);
    this.historiqueInterventions.set([]);
    this.erreurHistorique.set('');
    this.chargementHistorique.set(false);
    if (!installationId || !ticketId) return;
    const estValide = () => contexteValide() && requete === this.requeteHistorique;
    this.chargementHistorique.set(true);
    try {
      const response = await this.installationService.getInstallationInterventions(installationId);
      if (!estValide()) return;
      if (!response) throw new Error('Interventions indisponibles');
      this.historiqueInterventions.set(
        reunirInterventions(response)
          .filter((intervention) => Number(intervention.ticket?.id) === ticketId)
          .sort((a, b) =>
            (b.date_deb ?? b.date_intervention).localeCompare(a.date_deb ?? a.date_intervention),
          ),
      );
    } catch {
      if (estValide())
        this.erreurHistorique.set('Impossible de charger les interventions du ticket.');
    } finally {
      if (estValide()) this.chargementHistorique.set(false);
    }
  }

  ouvrirInterventionHistorique(intervention: InterventionBrute, event?: Event): void {
    // Le piège de focus doit mémoriser le déclencheur, même après un clic sous Safari.
    if (event?.currentTarget instanceof HTMLElement) event.currentTarget.focus();
    this.interventionSelectionnee.set({
      ...intervention,
      categorie: intervention.type === 'intervention_preventive' ? 'preventive' : 'curative',
      titre: `${intervention.nature === 'planification' ? 'Planification' : 'Intervention'} ${libelleTypeIntervention(intervention.type).toLocaleLowerCase('fr')}`,
    });
  }

  private mapperTicket(data: Record<string, unknown>): TicketCardItem {
    const ref = this.lireTexte(data['numero']) ?? '?';

    return {
      id: this.lireTexte(data['id']),
      reference: ref.startsWith('#') ? ref : `#${ref}`,
      issue: this.lireTexte(data['libelle']) ?? 'Ticket sans libellé',
      description: this.lireTexte(data['description']),
      dateDebut: formaterDateAvecJour(this.lireTexte(data['date_ouverture'])),
      dateFin: this.lireTexte(data['date_fermeture'])
        ? formaterDateAvecJour(this.lireTexte(data['date_fermeture']))
        : undefined,
      nbMessages: this.lireNombre(data['nbMessages']),
      vu: data['messages_non_lus'] !== true,
    };
  }

  private mapperEtat(etat?: string): TicketColumnState | null {
    switch (etat?.toLowerCase()) {
      case TicketState.nouveau:
        return TicketState.nouveau;
      case TicketState.encours:
        return TicketState.encours;
      case TicketState.enattente:
        return TicketState.enattente;
      case TicketState.resolu:
        return TicketState.resolu;
      default:
        return null;
    }
  }

  private lireTexte(v: unknown): string | undefined {
    if (typeof v === 'string') return v.trim() || undefined;
    if (typeof v === 'number') return String(v);
    return undefined;
  }

  private lireNombre(v: unknown): number | undefined {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const nombre = Number(v);
      if (Number.isFinite(nombre)) return nombre;
    }
    return undefined;
  }

  ajusterHauteurMessage() {
    const textarea = this.messageTextarea()?.nativeElement;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const styles = window.getComputedStyle(textarea);
    const lineHeight = Number.parseFloat(styles.lineHeight) || 20;
    const borderTop = Number.parseFloat(styles.borderTopWidth) || 0;
    const borderBottom = Number.parseFloat(styles.borderBottomWidth) || 0;
    const paddingTop = Number.parseFloat(styles.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(styles.paddingBottom) || 0;
    const maxHeight = lineHeight * 5 + paddingTop + paddingBottom + borderTop + borderBottom;

    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  onMessageInput() {
    this.erreurMessage.set('');
    this.ajusterHauteurMessage();
  }

  async envoyerMessage() {
    const estValide = this.contexteValide();
    const ticketId = this.ticketId;
    const contenu = this.message().trim();

    if (!ticketId || !contenu || this.envoiMessage()) return;

    this.envoiMessage.set(true);
    this.erreurMessage.set('');

    try {
      const ticket = await this.ticketService.envoyerMessageTicket(ticketId, contenu);
      if (!estValide()) return;
      if (!ticket) {
        this.erreurMessage.set("Le message n'a pas pu être envoyé.");
        return;
      }

      ++this.chargementTicketId;
      this.chargementFeed.set(false);
      this.appliquerTicketBrut(ticket);
      this.message.set('');
      requestAnimationFrame(() => this.ajusterHauteurMessage());
    } catch {
      if (!estValide()) return;
      this.erreurMessage.set("Le message n'a pas pu être envoyé.");
    } finally {
      if (estValide()) this.envoiMessage.set(false);
    }
  }

  onMessageKeydown(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      void this.envoyerMessage();
    }
  }

  toggleMasquerEvenements() {
    this.masquerEvenements.update((value) => !value);
  }

  toggleMasquerBonsIntervention() {
    this.masquerBonsIntervention.update((value) => !value);
  }

  formaterSeparateur(iso: string): string {
    const date = new Date(iso);
    const maintenant = new Date();
    const hier = new Date(maintenant);
    hier.setDate(maintenant.getDate() - 1);
    const memeJour = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    if (memeJour(date, maintenant)) return `Aujourd'hui, ${formaterHeure(date)}`;
    if (memeJour(date, hier)) return `Hier, ${formaterHeure(date)}`;
    return formaterDateHeure(date);
  }

  afficherSeparateur(index: number): boolean {
    const liste = this.feed();
    if (index === 0) return true;
    const prev = liste[index - 1].date;
    const curr = liste[index].date;
    return (
      prev.getFullYear() !== curr.getFullYear() ||
      prev.getMonth() !== curr.getMonth() ||
      prev.getDate() !== curr.getDate()
    );
  }

  initialesIntervenant(nom: string): string {
    return nom
      .split(' ')
      .map((m) => m[0] ?? '')
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  estMessageUtilisateurCourant(intervention: Intervention): boolean {
    const utilisateurId = this.utilisateurConnecteId();
    return (
      utilisateurId != null &&
      intervention.idIntervenant != null &&
      intervention.idIntervenant === utilisateurId
    );
  }

  estBonIntervention(intervention: Intervention): boolean {
    return intervention.nature === 'bon_intervention';
  }

  estInterventionSysteme(intervention: Intervention): boolean {
    return this.estBonIntervention(intervention);
  }

  formaterPhraseInterventionFeed(intervention: Intervention): string {
    if (this.estBonIntervention(intervention)) {
      return `Bon d'intervention : ${intervention.pb_resolu ? 'résolu' : 'non résolu'}`;
    }

    return intervention.commentaire?.trim() ?? '';
  }

  ouvrirInterventionModal(intervention: Intervention, event?: Event) {
    if (event?.currentTarget instanceof HTMLElement) event.currentTarget.focus();
    this.interventionSelectionnee.set(intervention);
  }

  editionBon(intervention: Intervention): InterventionEditable {
    return {
      id: intervention.id,
      nature: 'bon',
      auteurId: intervention.auteurId ?? null,
      valeurs: {
        ...creerBrouillonIntervention({
          installationId: this.installation()?.id,
          ticketId: Number(this.ticketId),
        }),
        description: intervention.commentaire ?? '',
        problemeResolu: intervention.pb_resolu === true,
      },
    };
  }

  fermerInterventionModal() {
    this.interventionSelectionnee.set(null);
  }

  titreInterventionModal(intervention: Intervention): string {
    if (this.estBonIntervention(intervention)) return "Bon d'intervention";
    return 'Intervention';
  }

  statutInterventionModal(intervention: Intervention): string {
    if (intervention.nature === 'planification') return 'Planifiée';
    if (this.estBonIntervention(intervention)) {
      return intervention.pb_resolu ? 'Résolu' : 'Non résolu';
    }
    if (intervention.titre) {
      return intervention.pb_resolu === true
        ? 'Clôturée'
        : intervention.pb_resolu === false
          ? 'Non clôturée'
          : 'Non renseigné';
    }
    return 'Information';
  }

  formaterChampEvenement(champ: string): string {
    switch (champ) {
      case 'etat':
        return 'Statut';
      default:
        return champ;
    }
  }

  formaterValeurEvenement(champ: string, valeur: string): string {
    if (champ === 'etat') return ticketCodeEtatLabel(valeur);
    return valeur;
  }

  private formaterValeurEvenementMinuscule(champ: string, valeur: string): string {
    return this.formaterValeurEvenement(champ, valeur).toLocaleLowerCase('fr-FR');
  }

  private formaterDateEvenementCourte(valeur?: string | null): string {
    if (!valeur) return '';

    const date = new Date(valeur);
    if (Number.isNaN(date.getTime())) return valeur;

    const jour = String(date.getDate()).padStart(2, '0');
    const mois = String(date.getMonth() + 1).padStart(2, '0');
    const annee = date.getFullYear();
    const heures = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${jour}-${mois}-${annee} ${heures}:${minutes}`;
  }

  estEvenementChangementStatut(evenement: TicketEvenement): boolean {
    return evenement.champ === 'etat' && !!evenement.valeur_apres;
  }

  estEvenementFermeture(evenement: TicketEvenement): boolean {
    return evenement.champ === 'ferme' && evenement.valeur_apres === '1';
  }

  estEvenementLibelleMetier(evenement: TicketEvenement): boolean {
    return evenement.champ === 'fin_pb';
  }

  private estEvenementMasque(evenement: TicketEvenement): boolean {
    //  evenements concernant le changement de la date de fin du problème ignorés
    return evenement.champ === 'fin_pb';
  }

  formaterAuteurEvenement(personne: TicketEvenement['personne']): string {
    if (!personne) return "Quelqu'un";
    if (typeof personne === 'string') return personne;

    const prenom = personne.prenom?.trim() ?? '';
    const nom = personne.nom?.trim() ?? '';
    return `${prenom} ${nom}`.trim() || "Quelqu'un";
  }

  formaterPhraseEvenement(evenement: TicketEvenement): string {
    const auteur = this.formaterAuteurEvenement(evenement.personne);

    if (this.estEvenementFermeture(evenement)) {
      return `${auteur} a fermé le ticket`;
    }

    if (this.estEvenementChangementStatut(evenement)) {
      return `${auteur} a passé le ticket au statut ${this.formaterValeurEvenement('etat', evenement.valeur_apres ?? '')}`;
    }

    return '';
  }

  formaterPhraseEvenementFeed(evenement: TicketEvenement): string {
    if (this.estEvenementFermeture(evenement)) {
      return 'Changement de statut : ticket résolu';
    }

    if (this.estEvenementChangementStatut(evenement)) {
      return `Changement de statut : ticket ${this.formaterValeurEvenementMinuscule('etat', evenement.valeur_apres ?? '')}`;
    }

    if (evenement.champ === 'fin_pb') {
      return `Date de fin du problème modifiée : ${this.formaterDateEvenementCourte(evenement.valeur_apres)}`;
    }

    const champ = this.formaterChampEvenement(evenement.champ);
    const valeur = this.formaterValeurEvenementMinuscule(
      evenement.champ,
      evenement.valeur_apres ?? '',
    );
    return `${champ} : ${valeur}`;
  }

  doitAfficherDateEntree(index: number): boolean {
    const feed = this.feed();
    if (index <= 0) return true;

    const entree = feed[index];
    const precedente = feed[index - 1];
    if (!entree || !precedente) return true;

    return entree.date.getTime() !== precedente.date.getTime();
  }

  private async chargerInstallation(id: number) {
    const contexteValide = this.contexteValide();
    const requete = ++this.chargementInstallationId;
    const estValide = () => contexteValide() && requete === this.chargementInstallationId;
    const courante = this.etat.installationCourante();
    if (courante?.id === id) {
      this.installation.set(courante);
      return;
    }
    this.chargement.set(true);
    try {
      const data = await this.installationService.getInstallation(id);
      if (!estValide()) return;
      this.installation.set(data);
      this.etat.installationCourante.set(data);
    } catch {
      if (!estValide()) return;
      this.erreurChargement.set("Impossible de charger l'état de l'installation.");
    } finally {
      if (estValide()) this.chargement.set(false);
    }
  }
}
